import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { marketImageUrl, marketTitle } from '../../lib/marketCatalog';
import { marketCategoryLabel } from '../../lib/marketCategories';
import { buildInquiryCsv, buildInquiryMessage, markRequested, REASONS, saveSupplierMeasures, sourceLink } from '../../lib/marketAdminWeights';
import { evidenceLine, HIDE_REASONS, latestDataReport, listHolds, PAGE, REASON_LABEL, releaseHolds, REVIEW_REASONS, VARIANT_REASONS, wouldHideTotal } from '../../lib/marketAdminHolds';

// Products hidden because their shipping data cannot be trusted (owner,
// 2026-10-29), and the variants hidden inside live products. Each hold shows
// its reason and the evidence. To bring one back: ask HioBuy (the same
// message / sheet / "sent" mark as «منتجات بدون وزن») and enter the weight
// they send — the server re-runs the checks and releases the hold by itself
// when the data is now good — or release it by hand with a note.
// The «للمراجعة» tab lists flags that hide nothing (专拍, sizes that cannot
// hold their weight). The 1688 link is admin-only.

const T = {
  ar: {
    title: 'منتجات محجوبة', sub: 'منتجات أو خيارات مخفية عن المتجر لأن بيانات شحنها غير موثوقة — مع السبب. اطلب الوزن من HioBuy وأدخله، فيرجع المنتج تلقائيًا إذا صارت بياناته سليمة، أو أرجعه يدويًا مع ملاحظة.',
    hide: 'مخفية', review: 'للمراجعة فقط', all: 'كل الأسباب', allCats: 'كل التصنيفات', search: 'ابحث بالاسم أو رقم 1688', go: 'بحث', clear: 'مسح',
    selectPage: 'تحديد الصفحة', selected: (n) => `${n} محدد`, copy: 'انسخ رسالة HioBuy', csv: 'حمّل ملف Excel', mark: 'علّمها كمُرسلة',
    release: 'أرجعها للمتجر', releaseOne: 'أرجع', notePrompt: 'سبب الإرجاع (إلزامي):', released: (p, v) => `رجع ${p} حجب و${v} خيار`,
    copied: 'نُسخت الرسالة', copyFail: 'تعذّر النسخ — انسخها من المربع', marked: (n) => `عُلّم ${n} كمُرسل`,
    variants: 'خيارات', heldVariants: (n) => `${n} خيار محجوب`, link: 'رابط 1688', none: 'لا شيء هنا.', prev: 'السابق', next: 'التالي',
    kg: 'الوزن كجم', l: 'الطول سم', w: 'العرض سم', h: 'الارتفاع سم', save: 'احفظ الوزن', saved: 'حُفظ — أُعيد الفحص',
    sentOn: 'أُرسل', answeredOn: 'وصل', heldOn: 'حُجب', pending: 'بانتظار المراجعة', msgTitle: 'نص الرسالة', close: 'إغلاق',
    by: { backfill: 'تنظيف البيانات', import: 'فحص الاستيراد', admin: 'يدوي', system: 'إعادة فحص' },
    nightly: (d, n, h, k, r) => `الفحص الليلي (${d}): ${n} منتج — كان سيخفي ${h}، ويصحّح ${k}، و${r} للمراجعة. للعرض فقط، لم يتغيّر شيء.`, nightlyFail: 'تعذّر الفحص الليلي الأخير:',
  },
  en: {
    title: 'Hidden products', sub: 'Products or variants kept off the store because their shipping data cannot be trusted — with the reason. Ask HioBuy for the weight and enter it: the product comes back by itself once its data is good. Or bring it back by hand with a note.',
    hide: 'Hidden', review: 'Review only', all: 'All reasons', allCats: 'All categories', search: 'Search by title or 1688 id', go: 'Search', clear: 'Clear',
    selectPage: 'Select page', selected: (n) => `${n} selected`, copy: 'Copy HioBuy message', csv: 'Download Excel sheet', mark: 'Mark as sent',
    release: 'Bring back to the store', releaseOne: 'Bring back', notePrompt: 'Why (required):', released: (p, v) => `${p} holds and ${v} variants released`,
    copied: 'Message copied', copyFail: 'Copy failed — copy it from the box', marked: (n) => `${n} marked as sent`,
    variants: 'variants', heldVariants: (n) => `${n} variants hidden`, link: '1688 link', none: 'Nothing here.', prev: 'Previous', next: 'Next',
    kg: 'Weight kg', l: 'Length cm', w: 'Width cm', h: 'Height cm', save: 'Save weight', saved: 'Saved — checks re-run',
    sentOn: 'Sent', answeredOn: 'Received', heldOn: 'Hidden', pending: 'Awaiting review', msgTitle: 'Message text', close: 'Close',
    by: { backfill: 'data clean-up', import: 'import check', admin: 'manual', system: 're-check' },
    nightly: (d, n, h, k, r) => `Nightly check (${d}): ${n} products — would hide ${h}, correct ${k}, flag ${r} for review. Report only; nothing was changed.`, nightlyFail: 'The last nightly check failed:',
  },
};
T.zh = T.en;
const day = (v) => (v ? String(v).slice(0, 10) : '');

function download(name, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ConsoleMarketHolds({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const L = REASON_LABEL[isAr ? 'ar' : 'en'];
  const [kind, setKind] = useState('hide');
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('');
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState({});
  const [msg, setMsg] = useState('');
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };
  const [nightly, setNightly] = useState(null);
  useEffect(() => { latestDataReport().then((r) => { if (r.ok) setNightly(r.data); }); }, []);

  const load = useCallback(async () => {
    setData(null); setErr('');
    const r = await listHolds({ reason, kind, category, q, offset });
    if (!r.ok) { setErr(r.error); setData({ rows: [], total: 0, counts: {}, categories: [] }); return; }
    setData(r.data);
  }, [reason, kind, category, q, offset]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const picked = Object.values(sel);
  const allOnPage = rows.length > 0 && rows.every((p) => sel[p.id]);
  const togglePage = () => setSel((s) => {
    const n = { ...s };
    if (allOnPage) rows.forEach((p) => delete n[p.id]); else rows.forEach((p) => { n[p.id] = p; });
    return n;
  });
  const toggle = (p) => setSel((s) => { const n = { ...s }; if (n[p.id]) delete n[p.id]; else n[p.id] = p; return n; });
  const filter = (set) => (v) => { set(v); setOffset(0); setSel({}); };

  const copyMessage = async () => {
    const text = buildInquiryMessage(picked);
    try { await navigator.clipboard.writeText(text); flash(c.copied); setMsg(''); } catch { setMsg(text); flash(c.copyFail); }
  };
  const mark = async () => {
    const r = await markRequested(picked.map((p) => p.id));
    if (!r.ok) { flash(r.error); return; }
    flash(c.marked(r.data)); setSel({}); load();
  };
  const release = async (ids, why) => {
    const note = window.prompt(c.notePrompt, '');
    if (!note || note.trim().length < 3) return;
    const r = await releaseHolds(ids, why, note);
    if (!r.ok) { flash(r.error); return; }
    flash(c.released(r.data.released_holds, r.data.released_variants)); setSel({}); load();
  };

  const counts = data?.counts || {};
  const [cats, setCats] = useState([]);
  useEffect(() => { if (data?.categories) setCats(data.categories); }, [data]);
  const reasons = kind === 'hide' ? HIDE_REASONS : REVIEW_REASONS;
  const total = Number(data?.total || 0);
  const fmt = (n) => Number(n).toLocaleString('en-US');
  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-holds">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{c.title}</h1>
            <p className="ac-sub">{c.sub}</p>
          </div>
        </div>

        {nightly && (
          <div className="ac-card" data-nightly-report={nightly.id} style={{ marginBottom: 12, fontSize: 12.5, color: nightly.error ? '#B4372C' : '#555' }}>
            {nightly.error ? `${c.nightlyFail} ${nightly.error}` : c.nightly(
              String(nightly.ran_at || '').slice(0, 16).replace('T', ' '),
              Number(nightly.checked || 0).toLocaleString('en-US'),
              wouldHideTotal(nightly),
              Object.values(nightly.would_correct || {}).reduce((a, n) => a + Number(n || 0), 0),
              Object.values(nightly.review || {}).reduce((a, n) => a + Number(n || 0), 0))}
          </div>
        )}

        <div className="ac-chiprow" style={{ marginBottom: 8 }}>
          {['hide', 'review'].map((k) => (
            <button key={k} type="button" data-hkind={k} className={`ac-chip${kind === k ? ' on' : ''}`} onClick={() => { filter(setKind)(k); setReason(''); }}>{c[k]}</button>
          ))}
        </div>
        <div className="ac-chiprow" style={{ marginBottom: 8 }}>
          <button type="button" data-hreason="" className={`ac-chip${reason === '' ? ' on' : ''}`} onClick={() => filter(setReason)('')}>{c.all}</button>
          {reasons.filter((k) => counts[k]).map((k) => (
            <button key={k} type="button" data-hreason={k} className={`ac-chip${reason === k ? ' on' : ''}`} onClick={() => filter(setReason)(k)}>
              {L[k] || k} ({fmt(counts[k])})
            </button>
          ))}
        </div>

        <div className="ac-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12, fontSize: 13 }}>
          <select value={category} data-h-category="1" onChange={(e) => filter(setCategory)(e.target.value)} style={{ height: 32, maxWidth: 220 }}>
            <option value="">{c.allCats}</option>
            {cats.map((x) => <option key={x.code} value={x.code}>{(marketCategoryLabel(x.code, lang) || x.code)} ({fmt(x.n)})</option>)}
          </select>
          <form style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flex: '1 1 240px' }} onSubmit={(e) => { e.preventDefault(); filter(setQ)(qDraft.trim()); }}>
            <input type="search" value={qDraft} data-h-search="1" maxLength={100} placeholder={c.search} onChange={(e) => setQDraft(e.target.value)} dir="auto" style={{ flex: 1, minWidth: 0, height: 32 }} />
            <button type="submit" className="ac-btn ac-btn-sm">{c.go}</button>
            {q && <button type="button" className="ac-btn ac-btn-sm" onClick={() => { setQDraft(''); filter(setQ)(''); }}>{c.clear}</button>}
          </form>
        </div>

        <div className="ac-card" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" data-select-page="1" checked={allOnPage} onChange={togglePage} />{c.selectPage}
          </label>
          <span data-selected={picked.length} style={{ fontSize: 12.5, color: '#666' }}>{c.selected(picked.length)}</span>
          <button type="button" className="ac-btn ac-btn-sm" data-copy-message="1" disabled={!picked.length} onClick={copyMessage}>{c.copy}</button>
          <button type="button" className="ac-btn ac-btn-sm" data-download-csv="1" disabled={!picked.length} onClick={() => download(`maabar-hidden-${new Date().toISOString().slice(0, 10)}.csv`, buildInquiryCsv(picked))}>{c.csv}</button>
          <button type="button" className="ac-btn ac-btn-sm" data-mark-sent="1" disabled={!picked.length} onClick={mark}>{c.mark}</button>
          <span style={{ flex: 1 }} />
          <button type="button" className="ac-btn ac-btn-sm" data-release-selected="1" disabled={!picked.length} onClick={() => release(picked.map((p) => p.id), reason)}>{c.release}</button>
        </div>

        {msg && (
          <div className="ac-card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><b>{c.msgTitle}</b><button type="button" className="ac-btn ac-btn-sm" onClick={() => setMsg('')}>{c.close}</button></div>
            <textarea readOnly dir="ltr" value={msg} style={{ width: '100%', minHeight: 180, fontSize: 12 }} onFocus={(e) => e.target.select()} />
          </div>
        )}

        {err && <div className="ac-card" style={{ color: '#B4372C', marginBottom: 12 }}>{err}</div>}
        {data == null ? <div className="ac-skel" style={{ height: 120 }} /> : rows.length === 0 ? <div className="ac-placeholder">{c.none}</div> : (
          <div data-hold-list={rows.length} style={{ display: 'grid', gap: 8 }}>
            {rows.map((p) => <HoldRow key={p.id} p={p} c={c} L={L} lang={lang} checked={!!sel[p.id]} onToggle={() => toggle(p)} flash={flash} onChanged={load} onRelease={release} />)}
          </div>
        )}
        {total > PAGE && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 14 }}>
            <button type="button" className="ac-btn ac-btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>{c.prev}</button>
            <span dir="ltr" style={{ fontSize: 12.5 }}>{offset + 1}–{Math.min(offset + PAGE, total)} / {fmt(total)}</span>
            <button type="button" className="ac-btn ac-btn-sm" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>{c.next}</button>
          </div>
        )}
      </div>
      {toast && <div className="ac-toast" data-toast="1">{toast}</div>}
    </ConsoleShell>
  );
}

function HoldRow({ p, c, L, lang, checked, onToggle, flash, onChanged, onRelease }) {
  const [v, setV] = useState({ kg: '', l: '', w: '', h: '' });
  const link = sourceLink(p);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    const n = (s) => (String(s).trim() === '' ? null : Number(String(s).replace(',', '.')));
    const r = await saveSupplierMeasures([{ product_id: p.id, unit_kg: n(v.kg), length_cm: n(v.l), width_cm: n(v.w), height_cm: n(v.h) }]);
    if (!r.ok) { flash(r.error); return; }
    const bad = r.data?.rejected?.[0];
    if (bad) { flash(REASONS[lang === 'ar' ? 'ar' : 'en'][bad.reason] || bad.reason); return; }
    flash(c.saved); onChanged();
  };
  const input = (k, ph) => <input value={v[k]} onChange={set(k)} placeholder={ph} inputMode="decimal" dir="ltr" data-h-input={k} style={{ width: 84, height: 30, fontSize: 12.5 }} />;
  const holds = p.holds || [];
  const vholds = p.variant_holds || [];
  const vByReason = vholds.reduce((m, x) => { (m[x.reason] = m[x.reason] || []).push(x); return m; }, {});
  return (
    <div className="ac-card" data-hold-product={p.id} style={{ display: 'grid', gridTemplateColumns: '22px 56px 1fr', gap: 10, alignItems: 'start' }}>
      <input type="checkbox" checked={checked} onChange={onToggle} data-select={p.id} style={{ marginTop: 4 }} />
      {p.image_ref ? <img src={marketImageUrl(p.image_ref, true)} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} /> : <div style={{ width: 56, height: 56, background: '#eee', borderRadius: 8 }} />}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{marketTitle(p, lang)}</div>
        <div style={{ fontSize: 12, color: '#666', margin: '2px 0 6px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <span>{marketCategoryLabel(p.category, lang) || '—'}</span>
          <span>{p.sku_count} {c.variants}</span>
          {p.review_status === 'pending_review' && <span>{c.pending}</span>}
          {link && <a href={link} target="_blank" rel="noopener noreferrer" data-source-link="1" dir="ltr">{c.link}</a>}
          {p.inq_status === 'requested' && <span data-inq="requested">{c.sentOn} {day(p.requested_at)}</span>}
          {p.inq_status === 'answered' && <span data-inq="answered">{c.answeredOn} {day(p.answered_at)}</span>}
        </div>
        {holds.map((h) => (
          <div key={h.reason} data-hold-reason={h.reason} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, marginBottom: 4 }}>
            <b style={{ color: h.kind === 'hide' ? '#B4372C' : '#9A7636' }}>{L[h.reason] || h.reason}</b>
            <span dir="auto" style={{ color: '#555' }}>{evidenceLine(h.evidence, lang)}</span>
            <span style={{ color: '#888' }}>{c.heldOn} {day(h.held_at)} · {c.by[h.source] || h.source}</span>
            <button type="button" className="ac-btn ac-btn-sm" data-release={`${p.id}:${h.reason}`} onClick={() => onRelease([p.id], h.reason)}>{c.releaseOne}</button>
          </div>
        ))}
        {Object.entries(vByReason).map(([r, list]) => (
          <div key={r} data-variant-reason={r} style={{ fontSize: 12.5, marginBottom: 4 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <b style={{ color: VARIANT_REASONS.includes(r) && r !== 'listing_review' ? '#B4372C' : '#9A7636' }}>{L[r] || r}</b>
              <span style={{ color: '#888' }}>{c.heldVariants(list.length)}</span>
              <button type="button" className="ac-btn ac-btn-sm" data-release={`${p.id}:${r}`} onClick={() => onRelease([p.id], r)}>{c.releaseOne}</button>
            </div>
            <ul style={{ margin: '2px 0 0', paddingInlineStart: 18, color: '#555' }}>
              {list.slice(0, 5).map((x) => <li key={x.sku_key} dir="auto">{evidenceLine(x.evidence, lang)}</li>)}
            </ul>
          </div>
        ))}
        {Number(p.variant_holds_n) > vholds.length && <div style={{ fontSize: 12, color: '#888' }}>+{Number(p.variant_holds_n) - vholds.length}</div>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          {input('kg', c.kg)}{input('l', c.l)}{input('w', c.w)}{input('h', c.h)}
          <button type="button" className="ac-btn ac-btn-sm" data-h-save={p.id} disabled={String(v.kg).trim() === ''} onClick={save}>{c.save}</button>
        </div>
      </div>
    </div>
  );
}
